import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeGesture } from '../../hooks/useTouchOptimizations';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { signOut } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHouse, 
  faList, 
  faComments, 
  faBook, 
  faGear, 
  faCircleQuestion,
  faRightFromBracket,
  faChevronDown,
  faChevronUp,
  faShield
} from '@fortawesome/free-solid-svg-icons';
import { sortExperiencesByDate, filterExperiencesByStatus } from '../../utils/experiences';
import { getExperienceType } from '../../data/experienceTypes';
import { useIsAdmin } from '../../utils/admin';

const navItems = [
  { id: 'dashboard', icon: faHouse, label: 'Dashboard', path: '/' },
  { id: 'experiences', icon: faList, label: 'Experiences', path: '/experiences' },
  { id: 'explore', icon: faBook, label: 'Explore', path: '/explore' },
  { id: 'chats', icon: faComments, label: 'Chats', path: '/chats' },
  { id: 'settings', icon: faGear, label: 'Settings', path: '/settings' },
];

export default function MobileDrawer({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { family, familyId } = useFamily();
  const { isAdmin } = useIsAdmin(user?.uid);
  const [activeExperiencesCount, setActiveExperiencesCount] = useState(0);
  const [unreadChatsCount, setUnreadChatsCount] = useState(0);
  const [showExperiencesMenu, setShowExperiencesMenu] = useState(false);
  const [recentExperiences, setRecentExperiences] = useState([]);
  const [profileImageError, setProfileImageError] = useState(false);
  const drawerRef = useRef(null);

  // Reset profile image error when user or photoURL changes
  useEffect(() => {
    setProfileImageError(false);
  }, [user?.photoURL]);

  // Swipe to close drawer (swipe right)
  useSwipeGesture(
    null, // onSwipeLeft - do nothing
    () => {
      if (isOpen) {
        onClose();
      }
    }, // onSwipeRight - close drawer
    100, // threshold
    drawerRef.current
  );

  // Close drawer on route change (but not on initial mount)
  const prevPathname = useRef(location.pathname);
  useEffect(() => {
    if (isOpen && location.pathname !== prevPathname.current) {
      prevPathname.current = location.pathname;
      onClose();
    } else {
      prevPathname.current = location.pathname;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Load experiences (for count and quick switcher)
  useEffect(() => {
    if (!familyId) return;

    const q = query(
      collection(db, 'projects'),
      where('familyId', '==', familyId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const experiences = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Count active experiences (planning or upcoming status)
      const active = experiences.filter(e => {
        const status = e.status || 'planning';
        return status === 'planning' || status === 'upcoming';
      });
      setActiveExperiencesCount(active.length);
      
      // Get recent experiences (sorted by updatedAt, max 5)
      const recent = sortExperiencesByDate(
        filterExperiencesByStatus(experiences, 'active')
      ).slice(0, 5);
      setRecentExperiences(recent);
    }, (error) => {
      console.error('Error loading experiences:', error);
    });

    return unsubscribe;
  }, [familyId]);

  // Load unread chats count (placeholder)
  useEffect(() => {
    setUnreadChatsCount(0);
  }, [familyId]);

  const getActiveRoute = () => {
    const path = location.pathname;
    if (path === '/') return 'dashboard';
    if (path === '/experiences' || path.startsWith('/experience') || path.startsWith('/project')) return 'experiences';
    if (path.startsWith('/chats')) return 'chats';
    if (path.startsWith('/explore')) return 'explore';
    if (path.startsWith('/settings')) return 'settings';
    if (path.startsWith('/admin')) return 'admin';
    return 'dashboard';
  };

  const activeRoute = getActiveRoute();

  const getBadgeCount = (itemId) => {
    if (itemId === 'experiences') return activeExperiencesCount > 0 ? activeExperiencesCount : null;
    if (itemId === 'chats') return unreadChatsCount > 0 ? unreadChatsCount : null;
    return null;
  };

  const handleNavClick = (item) => {
    // Always navigate to the page
    navigate(item.path);
    setShowExperiencesMenu(false);
  };

  const handleExperienceClick = (experienceId) => {
    navigate(`/experience/${experienceId}`);
    setShowExperiencesMenu(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      toast.success('Signed out successfully');
      navigate('/login');
      onClose();
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not children
    e.preventDefault();
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] md:hidden" style={{ zIndex: 10000 }}>
      {/* Backdrop overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/50"
        onClick={handleBackdropClick}
      />

      {/* Drawer */}
      <motion.div
        ref={drawerRef}
        initial={{ x: '-100%' }}
        animate={{ x: 0 }}
        exit={{ x: '-100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl overflow-y-auto momentum-scroll safe-top safe-bottom"
        onClick={(e) => e.stopPropagation()}
      >
            {/* Drawer Header */}
            <div className="border-b border-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-serif font-bold text-text-primary">
                  Menu
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Close menu"
                >
                  <svg 
                    className="w-6 h-6 text-text-secondary" 
                    fill="none" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User/Family Info */}
              <div className="flex items-center gap-3">
                {user?.photoURL && !profileImageError ? (
                  <img 
                    src={user.photoURL} 
                    alt={user.displayName || 'Profile'} 
                    className="w-12 h-12 rounded-full flex-shrink-0 object-cover"
                    onError={() => {
                      console.error('Profile picture failed to load:', user.photoURL);
                      setProfileImageError(true);
                    }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold text-lg">
                    {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'J'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-text-primary truncate">
                    {family?.name || 'Your Family'}
                  </p>
                  <p className="text-sm text-text-secondary truncate">
                    {user?.displayName || user?.email || 'User'}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Items */}
            <nav className="p-4">
              <div className="space-y-1">
                {navItems.map((item) => {
                  const isActive = activeRoute === item.id;
                  const badgeCount = getBadgeCount(item.id);
                  const isExperiences = item.id === 'experiences';
                  const showExpandArrow = isExperiences && recentExperiences.length > 0;
                  
                  return (
                    <div key={item.id}>
                      <div className="flex items-center gap-1">
                        <motion.button
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleNavClick(item)}
                          className={`
                            flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-xl transition-all touch-target flex-1
                            ${isActive 
                              ? 'bg-primary text-white font-semibold' 
                              : 'text-text-secondary hover:bg-gray-100 hover:text-text-primary active:bg-gray-50'
                            }
                          `}
                        >
                          <FontAwesomeIcon 
                            icon={item.icon} 
                            className="w-5 h-5 flex-shrink-0"
                          />
                          <span className="flex-1 text-left">{item.label}</span>
                          {badgeCount !== null && !showExpandArrow && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                              isActive 
                                ? 'bg-white/20 text-white' 
                                : 'bg-primary text-white'
                            }`}>
                              {badgeCount}
                            </span>
                          )}
                        </motion.button>
                        {showExpandArrow && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowExperiencesMenu(!showExperiencesMenu);
                            }}
                            className={`
                              p-2 rounded-xl transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center touch-target
                              ${isActive 
                                ? 'text-white hover:bg-white/20' 
                                : 'text-text-secondary hover:bg-gray-100'
                              }
                            `}
                            aria-label="Show recent experiences"
                          >
                            <FontAwesomeIcon 
                              icon={showExperiencesMenu ? faChevronUp : faChevronDown} 
                              className="w-3 h-3"
                            />
                          </button>
                        )}
                      </div>
                      
                      {/* Experiences Quick Switcher Menu */}
                      {isExperiences && showExperiencesMenu && recentExperiences.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="ml-4 mr-2 mt-1 mb-2 bg-surface rounded-lg border border-border overflow-hidden"
                        >
                          <div className="px-3 py-2 text-xs font-semibold text-text-secondary uppercase border-b border-border">
                            Recent Experiences
                          </div>
                          {recentExperiences.map((experience) => (
                            <button
                              key={experience.id}
                              onClick={() => handleExperienceClick(experience.id)}
                              className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-[#E0E0E0] transition-colors flex items-center justify-between min-h-[44px]"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{experience.title}</div>
                                <div className="text-xs text-text-secondary">
                                  {getExperienceType(experience.type)?.name || experience.type}
                                </div>
                              </div>
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  );
                })}
                
                {/* Admin Link - only visible to admins */}
                {isAdmin && (
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/admin')}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-xl transition-all touch-target
                      ${location.pathname === '/admin'
                        ? 'bg-primary text-white font-semibold' 
                        : 'text-text-secondary hover:bg-gray-100 hover:text-text-primary active:bg-gray-50'
                      }
                    `}
                  >
                    <FontAwesomeIcon 
                      icon={faShield} 
                      className="w-5 h-5 flex-shrink-0"
                    />
                    <span className="flex-1 text-left">Admin</span>
                  </motion.button>
                )}
              </div>
            </nav>

            {/* Divider */}
            <div className="border-t border-border mx-4 my-4" />

            {/* Secondary Actions */}
            <div className="px-4 pb-6 space-y-1">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  toast.info('Help & Support coming soon');
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-xl text-text-secondary hover:bg-gray-100 hover:text-text-primary transition-all touch-target active:bg-gray-50"
              >
                <FontAwesomeIcon icon={faCircleQuestion} className="w-5 h-5" />
                <span className="flex-1 text-left">Help & Support</span>
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-xl text-primary hover:bg-primary/10 transition-all touch-target active:bg-primary/20"
              >
                <FontAwesomeIcon icon={faRightFromBracket} className="w-5 h-5" />
                <span className="flex-1 text-left font-semibold">Sign Out</span>
              </motion.button>
            </div>

            {/* App Version (optional) */}
            <div className="px-6 pb-6 text-xs text-text-secondary text-center">
              Jewish Life Platform v1.0
            </div>
          </motion.div>
    </div>
  );
}

